// pages/dashboard/DashboardPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, Grid, Button, Chip, IconButton,
  alpha, CircularProgress, Tooltip, Paper, Stack, Fade, Grow, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon, CheckCircleOutline as CheckIcon,
  AccountBalanceWallet as WalletIcon, TrendingUp as SalesIcon,
  ShoppingCart as PurchaseIcon, MoneyOff as DueIcon,
  Store as RetailIcon, LocalShipping as WholesaleIcon,
  AttachMoney as CashIcon, Payments as CostIcon,
  WbSunny as MorningIcon, WbTwilight as EveningIcon, Brightness2 as NightIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { reportService, DashboardStats } from '../../services/reportService';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt2 = (v: number): string =>
  v.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 });

const getGreeting = (): { text: string; icon: React.ReactNode } => {
  const h = new Date().getHours();
  if (h < 12) return { text: 'Good morning',   icon: <MorningIcon /> };
  if (h < 17) return { text: 'Good afternoon', icon: <SalesIcon /> };
  return       { text: 'Good evening',   icon: <NightIcon /> };
};

const today = () => new Date().toLocaleDateString('en-US', {
  weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
});

// ── Stat Card ─────────────────────────────────────────────────────────────────
interface StatCardProps {
  label:     string;
  sublabel?: string;
  value:     string | number;
  icon:      React.ReactNode;
  color:     string;
  prefix?:   string;
  index:     number;
  size?:     'large' | 'normal';
  subStats?: { label: string; value: string; color: string }[];
}

const StatCard: React.FC<StatCardProps> = ({
  label, sublabel, value, icon, color, prefix = 'Tk.  ', index, size = 'normal', subStats,
}) => (
  <Grow in timeout={400 + index * 100} style={{ height: '100%' }}>
    <Card elevation={0} sx={{
      p: { xs: 2, md: 2.5 }, borderRadius: 3, background: '#fff',
      border: `1px solid ${alpha(color, 0.18)}`,
      boxShadow: `0 2px 12px ${alpha(color, 0.07)}`,
      position: 'relative', overflow: 'hidden',
      height: '100%',           
      display: 'flex',          
      flexDirection: 'column', 
      transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: `0 12px 32px ${alpha(color, 0.18)}`,
        borderColor: alpha(color, 0.4),
        '&::before': { transform: 'scaleX(1)' },
      },
      '&::before': {
        content: '""', position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.5)})`,
        transform: 'scaleX(0)', transformOrigin: 'left', transition: 'transform 0.3s ease',
      },
      '&::after': {
        content: '""', position: 'absolute', top: -20, right: -20, width: 80, height: 80,
        borderRadius: '50%', background: `radial-gradient(circle, ${alpha(color, 0.07)} 0%, transparent 70%)`,
        pointerEvents: 'none',
      },
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.78rem', mb: 0.5 }}>
            {label}
          </Typography>
          {sublabel && (
            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5 }}>
              {sublabel}
            </Typography>
          )}
          <Typography variant="h5" sx={{
            fontWeight: 800, color,
            fontSize: size === 'large' ? { xs: '1.4rem', md: '1.75rem' } : { xs: '1.2rem', md: '1.5rem' },
            lineHeight: 1.1,
          }}>
            {prefix}{typeof value === 'number' ? fmt2(value) : value}
          </Typography>

          {subStats && subStats.length > 0 && (
            <Stack direction="row" spacing={1.5} sx={{ mt: 1.5 }} flexWrap="wrap">
              {subStats.map((s, i) => (
                <Box key={i}>
                  <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', fontSize: '0.68rem' }}>
                    {s.label}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: s.color, fontSize: '0.8rem' }}>
                    Tk.  {s.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>

        <Box sx={{
          width: 48, height: 48, borderRadius: '14px',
          background: alpha(color, 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color, flexShrink: 0, '& svg': { fontSize: 24 },
        }}>
          {icon}
        </Box>
      </Box>
    </Card>
  </Grow>
);

// ── Quick Action ──────────────────────────────────────────────────────────────
const QUICK_ACTIONS = [
  { label: 'Purchase Entry',  description: 'Add new purchase records',           color: '#2196F3', path: '/entry-form/purchase-info' },
  { label: 'Selling Entry',   description: 'Record a new sale',                  color: '#4CAF50', path: '/entry-form/selling-info'  },
  { label: 'Return Entry',    description: 'Log a product return',               color: '#FF9800', path: '/entry-form/return-info'   },
  { label: 'Cost Entry',      description: 'Record an expense',                  color: '#9C27B0', path: '/entry-form/cost-info'     },
  { label: 'Stock Report',    description: 'View current stock levels',          color: '#667EEA', path: '/reports/stock-info'       },
  { label: 'Pivot Report',    description: 'Seller × product sales pivot',       color: '#E91E63', path: '/reports/pivot-info'       },
];

// ── Main ─────────────────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const greeting   = getGreeting();

  const [stats, setStats]       = useState<DashboardStats | null>(null);
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const fetchStats = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await reportService.getDashboard();
      setStats(res.data);
      setLastRefresh(new Date());
    } catch {} finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const firstName = user?.users_name?.split(' ')[0] || '';

  // ── Loading skeleton ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={40} sx={{ color: '#667EEA' }} />
      </Box>
    );
  }

  const s = stats;

  return (
    <Fade in timeout={500}>
      <Box>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
              <Box sx={{ color: '#FF9800' }}>{greeting.icon}</Box>
              <Typography variant="h4" fontWeight={800} sx={{
                fontSize: { xs: '1.4rem', md: '1.75rem' },
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
              }}>
                {greeting.text}, {firstName}!
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">{today()}</Typography>
          </Box>

          <Button size="small" variant="outlined" onClick={() => fetchStats(true)}
            startIcon={refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
            disabled={refreshing}
            sx={{ borderRadius: 2, borderColor: alpha('#667eea', 0.4), color: '#667eea', fontWeight: 600 }}>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </Box>

        {/* ── Row 1: Key financial cards ─────────────────────────────────── */}
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 4, height: 22, borderRadius: 1, background: 'linear-gradient(180deg,#667eea,#764ba2)' }} />
            <Typography variant="h6" fontWeight={700} fontSize="1rem">Today's Financial Overview</Typography>
            {!loading && (
              <Chip size="small" label={`Updated ${lastRefresh.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`}
                icon={<CheckIcon style={{ fontSize: 12 }} />}
                sx={{ ml: 'auto', height: 22, fontSize: '0.68rem', background: alpha('#4CAF50', 0.1), color: '#4CAF50',
                  border: `1px solid ${alpha('#4CAF50', 0.2)}`, '& .MuiChip-icon': { color: '#4CAF50' } }} />
            )}
          </Box>

          <Grid container spacing={2} alignItems="stretch">
            {/* Card 1: Today's Purchase Amount */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                index={3} size="large"
                label="Today's Purchase Amount"
                sublabel="Total purchase value today"
                value={s?.today_purchase_amount ?? 0}
                icon={<PurchaseIcon />}
                color="#FF9800"
              />
            </Grid>

            {/* Card 2: Today's Sales Amount */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard index={4} label="Today's Sales Amount" sublabel="Cash + Due" value={s?.today_total_sales ?? 0}
                icon={<SalesIcon />} color="#667EEA" />
            </Grid>

            {/* Card 2: Sales — Cash (Retail/Wholesale) */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                index={1} size="large"
                label="Today's Cash Sales"
                sublabel="Retail + Wholesale cash received"
                value={s?.today_cash_sales ?? 0}
                icon={<CashIcon />}
                color="#2196F3"
                subStats={[
                  { label: 'Retail',    value: fmt2(s?.today_retail_cash    ?? 0), color: '#2196F3' },
                  { label: 'Wholesale', value: fmt2(s?.today_wholesale_cash ?? 0), color: '#9C27B0' },
                ]}
              />
            </Grid>

            {/* Card 3: Sales — Due (Retail/Wholesale) */}
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <StatCard
                index={2} size="large"
                label="Today's Due Amount"
                sublabel="Outstanding dues today"
                value={s?.today_due_sales ?? 0}
                icon={<DueIcon />}
                color="#f44336"
                subStats={[
                  { label: 'Retail',    value: fmt2(s?.today_retail_due    ?? 0), color: '#f44336' },
                  { label: 'Wholesale', value: fmt2(s?.today_wholesale_due ?? 0), color: '#E91E63' },
                ]}
              />
            </Grid>
          </Grid>
        </Box>

        {/* ── Row 2: Supporting metrics ───────────────────────────────────── */}
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard index={6} label="Retail Sales" value={fmt2((s?.today_retail_cash ?? 0) + (s?.today_retail_due ?? 0))}
              icon={<RetailIcon />} color="#00BCD4"/>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard index={7} label="Wholesale Sales" value={fmt2((s?.today_wholesale_cash ?? 0) + (s?.today_wholesale_due ?? 0))}
              icon={<WholesaleIcon />} color="#FF5722"/>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard index={5} label="Today's Cost" sublabel="All expenses" value={s?.today_cost_amount ?? 0}
              icon={<CostIcon />} color="#9C27B0" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <StatCard
              index={0} size="large"
              label="Today's Cash in Hand"
              sublabel="Today's cash sales + cash paid - costs"
              value={s?.cash_in_hand ?? 0}
              icon={<WalletIcon />}
              color="#4CAF50"
            />
          </Grid>
          {/* <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard index={8} label="Total Entries" sublabel="All modules" value={s?.today_total_entries ?? 0}
              icon={<CheckIcon />} color="#607D8B" prefix="" />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 2 }}>
            <StatCard index={9} label="Pending Sales" sublabel="Due > 0" value={s?.today_pending ?? 0}
              icon={<DueIcon />} color="#FF9800" prefix="" />
          </Grid>*/}
        </Grid>

    {/* ── Row 3:  ───────────────────────────────────── 
        <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <StatCard
              index={0} size="large"
              label="Today's Cash in Hand"
              sublabel="Today's cash sales + cash paid - costs"
              value={s?.cash_in_hand ?? 0}
              icon={<WalletIcon />}
              color="#4CAF50"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 6 }}>
            <StatCard
              index={0} size="large"
              label="Total Petty Cash"
              sublabel="Petty cash balance"
              value={s?.petty_cash ?? 0}
              icon={<WalletIcon />}
              color="#4CAF50"
            />
          </Grid>
        </Grid>
        */}
        {/* ── Quick Actions ───────────────────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Box sx={{ width: 4, height: 22, borderRadius: 1, background: 'linear-gradient(180deg,#667eea,#764ba2)' }} />
            <Typography variant="h6" fontWeight={700} fontSize="1rem">Quick Actions</Typography>
          </Box>
          <Grid container spacing={2}>
            {QUICK_ACTIONS.map((a, i) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={a.label}>
                <Grow in timeout={600 + i * 80}>
                  <Card elevation={0} onClick={() => navigate(a.path)} sx={{
                    p: 2.5, borderRadius: 3, background: '#fff', cursor: 'pointer',
                    border: `1px solid ${alpha(a.color, 0.18)}`,
                    transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: `0 14px 36px ${alpha(a.color, 0.18)}`,
                      borderColor: alpha(a.color, 0.5),
                      '& .qa-icon': { background: a.color, color: '#fff', transform: 'scale(1.1) rotate(5deg)' },
                    },
                  }}>
                    <Box className="qa-icon" sx={{
                      width: 48, height: 48, borderRadius: '13px',
                      background: alpha(a.color, 0.1), color: a.color,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      mb: 1.5, transition: 'all 0.3s ease',
                      '& svg': { fontSize: 24 },
                    }}>
                      {i < 4 ? <PurchaseIcon /> : <SalesIcon />}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={0.5}>{a.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{a.description}</Typography>
                  </Card>
                </Grow>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* ── Bottom info strip ───────────────────────────────────────────── */}
        <Paper elevation={0} sx={{
          p: 2, borderRadius: 3, background: alpha('#667eea', 0.04),
          border: `1px solid ${alpha('#667eea', 0.1)}`,
          display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#4CAF50',
              animation: 'pulse 2s ease-in-out infinite',
              '@keyframes pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.4 } } }} />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>System Online</Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Typography variant="caption" color="text.secondary">
            Role: <strong>{user?.role?.role_name?.replace(/_/g, ' ')}</strong>
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography variant="caption" color="text.secondary">
            Designation: <strong>{user?.designation}</strong>
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" color="text.secondary">
              © {new Date().getFullYear()} SP1 Global Enterprise
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Fade>
  );
};

export default DashboardPage;